import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { DataTableBlock } from "@sylviq/ui-react-web/blocks/table";
import { Link } from "@tanstack/react-router";
import type { ActionMenuItem } from "@/components/ActionMenu";
import { InvoiceRowActions } from "./InvoiceRowActions";
import { CreditNoteCreateDrawer } from "@/features/credit-notes/CreditNoteCreateDrawer";
import { RecurringInvoicesPage } from "@/features/recurring/RecurringInvoicesPage";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CardField } from "@/components/RecordCard";
import { WorklistSummary } from "@/components/WorklistSummary";
import {
  PaymentTermPicker,
  usePaymentTerms,
} from "@/features/payment-terms/PaymentTermPicker";
import { dateInputToUtcIso } from "@/features/payment-terms/paymentTermDate";
import { DocumentLineRow } from "@/features/document-lines/DocumentLineRow";
import { totalTaxableLines } from "@/lib/tax";
import { useOrganization } from "@/lib/organization";
import {
  createInvoice,
  createInvoicePortalToken,
  downloadInvoicePdf,
  getInvoiceSummary,
  type CreditNote,
  type Invoice,
  type InvoiceSummary,
  listCreditNotes,
  listInvoices,
  listTaxRates,
  portalInvoiceUrl,
  previewReminders,
  type TaxRate,
  transitionInvoice,
} from "@/lib/api";
import { useViewSearch } from "@/lib/view-search";
import {
  canMarkInvoiceSent,
  canRemindInvoice,
  invoiceListStatus,
  isSelectableInvoice,
  matchesInvoiceListFilter,
  payableBalanceCents,
} from "@/lib/worklist";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import { toMinor } from "@/features/document-lines/productDetails";
import {
  toInvoiceLinePayload,
  type InvoiceLineDraft,
} from "./invoiceForm";

function statusTone(
  status: string,
): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "Paid":
      return "success";
    case "Overdue":
      return "danger";
    case "Awaiting payment":
    case "Part paid":
      return "warning";
    default:
      return "neutral";
  }
}

function InvoiceCreditNoteCell({
  notes,
  invoiceId,
}: {
  notes: CreditNote[];
  invoiceId: string;
}) {
  const visible = notes.filter((note) => {
    const status = note.status.toLowerCase();
    return status === "draft" || status === "sent" || status === "applied";
  });
  if (visible.length === 0) {
    return (
      <span className="text-xs text-[var(--muted-foreground)]">None</span>
    );
  }
  return (
    <div className="grid gap-1">
      {visible.map((note) => {
        const applied = note.status.toLowerCase() === "applied";
        return (
          <Link
            key={note.id}
            to="/credit-notes/$creditNoteId"
            params={{ creditNoteId: note.id }}
            title={
              applied
                ? `${note.credit_note_number} is applied and readonly`
                : note.status.toLowerCase() === "sent"
                  ? `${note.credit_note_number} is sent and ready to apply`
                  : `${note.credit_note_number} is a draft`
            }
            className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
          >
            {applied ? "Applied" : "Draft"}
          </Link>
        );
      })}
      <span className="sr-only">Credit note for {invoiceId}</span>
    </div>
  );
}

const INVOICE_STATUS_FILTERS = [
  "draft",
  "sent",
  "overdue",
  "paid",
  "void",
] as const;

type InvoiceStatusFilter = "" | (typeof INVOICE_STATUS_FILTERS)[number];
type InvoiceViewTab = "invoices" | "recurring";

interface KnownCustomer {
  name: string;
  crmContactId?: string;
}

const INVOICE_DRAFT_PREFIX = "niche-accounting.invoice-draft.v1";

export function InvoicesPage() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [portalBusy, setPortalBusy] = useState<string | null>(null);
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  const { terms: paymentTerms, error: paymentTermsError } = usePaymentTerms();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  useEffect(() => {
    let cancelled = false;
    listTaxRates()
      .then((rows) => {
        if (!cancelled) setTaxRates(rows);
      })
      .catch(() => {
        if (!cancelled) setTaxRates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/sort params — filtering happens here).
  const [viewTab, setViewTab] = useViewSearch<InvoiceViewTab>(
    "tab",
    ["invoices", "recurring"],
    "invoices",
  );
  const [statusFilter, setStatusFilter] = useViewSearch<InvoiceStatusFilter>(
    "status",
    INVOICE_STATUS_FILTERS,
    "",
  );
  const [applyNotice, setApplyNotice] = useState(false);
  const [appliedJournalId, setAppliedJournalId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<"due" | "total" | "number" | "customer">(
    "due",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Worklist summary (outstanding/overdue/open + aging). Separate from the
  // list so a failed rollup read shows an honest error, never a fake zero.
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Credit-note states are shown on the invoice row.
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [creditInvoiceId, setCreditInvoiceId] = useState<string | null>(null);
  const [creditNotice, setCreditNotice] = useState<string | null>(null);

  // Bulk selection over the filtered, eligible rows.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<
    null | "reminder" | "mark-sent"
  >(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  // Draft mirror: load from this browser session when the organization is known,
  // then save on every change. A failed storage read yields a fresh draft.
  useEffect(() => {
    setDraft(loadDocumentDraft(INVOICE_DRAFT_PREFIX, organizationId) ?? newDocumentDraft());
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(INVOICE_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    setSummaryError(null);
    getInvoiceSummary()
      .then(setSummary)
      .catch((cause: unknown) =>
        setSummaryError(cause instanceof Error ? cause.message : String(cause)),
      )
      .finally(() => setSummaryLoading(false));
  }, []);

  const reload = useCallback(() => {
    listInvoices()
      .then(setInvoices)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : String(cause)),
      );
    listCreditNotes()
      .then(setCreditNotes)
      .catch(() => setCreditNotes([]));
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    reload();
  }, [reload]);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (index: number, patch: Partial<InvoiceLineDraft>) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const removeLine = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      lines:
        prev.lines.length <= 1
          ? prev.lines
          : prev.lines.filter((_, i) => i !== index),
    }));
  };

  // Live estimate over the lines that currently parse; invalid rows are
  // excluded (they block submit with a validation message instead). Tax is an
  // estimate only — the server re-computes it from each line's tax_rate_id.
  const taxRateBpById = useMemo(
    () => new Map(taxRates.map((rate) => [rate.id, rate.rate_bp])),
    [taxRates],
  );
  const totals = totalTaxableLines(
    draft.lines.map((line) => ({
      amountCents: toMinor(line.amount) ?? 0,
      rateBp: line.tax_rate_id
        ? taxRateBpById.get(line.tax_rate_id)
        : undefined,
    })),
  );

  // Readable customer selection: names already used on this organization's
  // invoices, with their CRM link carried along when the customer matches.
  const knownCustomers = useMemo<KnownCustomer[]>(() => {
    const byName = new Map<string, KnownCustomer>();
    for (const invoice of invoices ?? []) {
      const name = invoice.customer_name.trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const existing = byName.get(key);
      if (!existing) byName.set(key, { name, crmContactId: invoice.crm_contact_id });
      else if (!existing.crmContactId && invoice.crm_contact_id)
        existing.crmContactId = invoice.crm_contact_id;
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  const selectCustomer = (name: string) => {
    const match = knownCustomers.find(
      (customer) => customer.name.toLowerCase() === name.trim().toLowerCase(),
    );
    updateDraft({
      partyName: name,
      crmContactId: match?.crmContactId ?? "",
    });
  };

  // Currency for amounts shown only in places without a single document
  // (the summary strip); rows fall back per-document below.
  const listCurrency =
    invoices?.find((invoice) => invoice.currency_code)?.currency_code || "USD";

  const creditNotesByInvoice = useMemo(() => {
    const byInvoice = new Map<string, CreditNote[]>();
    for (const note of creditNotes) {
      if (!note.related_invoice_id) continue;
      const rows = byInvoice.get(note.related_invoice_id) ?? [];
      rows.push(note);
      byInvoice.set(note.related_invoice_id, rows);
    }
    return byInvoice;
  }, [creditNotes]);

  const statusCounts = (invoices ?? []).reduce<Record<string, number>>(
    (counts, invoice) => {
      const key = invoice.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const sentCount = (invoices ?? []).filter((invoice) =>
    matchesInvoiceListFilter(invoice, "sent"),
  ).length;
  const overdueCount = (invoices ?? []).filter((invoice) =>
    matchesInvoiceListFilter(invoice, "overdue"),
  ).length;
  // Client-side filter + sort over the fetched list.
  const visibleInvoices = useMemo(
    () =>
      (invoices ?? [])
        .filter((invoice) => matchesInvoiceListFilter(invoice, statusFilter))
        .filter((invoice) => {
          const query = search.trim().toLowerCase();
          if (!query) return true;
          return (
            invoice.invoice_number.toLowerCase().includes(query) ||
            invoice.customer_name.toLowerCase().includes(query)
          );
        })
        .filter((invoice) => {
          const day = invoice.due_at.slice(0, 10);
          if (dateFrom && day < dateFrom) return false;
          if (dateTo && day > dateTo) return false;
          return true;
        })
        .sort((a, b) => {
          switch (sort) {
            case "total":
              return b.total_minor - a.total_minor;
            case "number":
              return a.invoice_number.localeCompare(b.invoice_number);
            case "customer":
              return a.customer_name.localeCompare(b.customer_name);
            default:
              return a.due_at.localeCompare(b.due_at);
          }
        }),
    [invoices, statusFilter, search, dateFrom, dateTo, sort],
  );

  // Rows that can participate in a bulk action, and selection pruned to the
  // currently filtered view (changing filters drops hidden selections).
  const selectableVisible = useMemo(
    () => visibleInvoices.filter(isSelectableInvoice),
    [visibleInvoices],
  );

  useEffect(() => {
    setSelectedIds((previous) => {
      if (previous.size === 0) return previous;
      const allowed = new Set(selectableVisible.map((invoice) => invoice.id));
      const next = new Set([...previous].filter((id) => allowed.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [selectableVisible]);

  const selectedInvoices = useMemo(
    () => visibleInvoices.filter((invoice) => selectedIds.has(invoice.id)),
    [visibleInvoices, selectedIds],
  );
  const reminderTargets = selectedInvoices.filter(canRemindInvoice);
  const draftTargets = selectedInvoices.filter(canMarkInvoiceSent);

  const bulkActions: ActionMenuItem[] = [
    {
      id: "mark-sent",
      label: `Mark selected as sent${
        draftTargets.length > 0 ? ` (${draftTargets.length})` : ""
      }`,
      disabled: bulkBusy || draftTargets.length === 0,
      onSelect: () => setBulkConfirm("mark-sent"),
    },
    {
      id: "check-reminders",
      label: `Check reminders${
        reminderTargets.length > 0 ? ` (${reminderTargets.length})` : ""
      }`,
      disabled: bulkBusy || reminderTargets.length === 0,
      onSelect: () => setBulkConfirm("reminder"),
    },
    {
      id: "clear-selection",
      label: "Clear selection",
      disabled: selectedIds.size === 0,
      onSelect: () => setSelectedIds(new Set()),
    },
  ];

  const runBulkAction = async () => {
    if (!bulkConfirm) return;
    setBulkBusy(true);
    setError(null);
    setBulkNotice(null);
    try {
      if (bulkConfirm === "mark-sent") {
        const targets = draftTargets;
        await Promise.all(
          targets.map((invoice) => transitionInvoice(invoice.id, "sent")),
        );
        setBulkNotice(
          `Marked ${targets.length} draft invoice${targets.length === 1 ? "" : "s"} as sent.`,
        );
      } else {
        const targets = reminderTargets;
        const candidates = await previewReminders({
          document_type: "invoice",
          trigger: "after_due",
          trigger_days: 1,
          recipient: "contact_email",
        });
        const list = Array.isArray(candidates) ? candidates : [];
        const matched = list.filter((candidate) =>
          targets.some((invoice) => invoice.id === candidate.document_id),
        ).length;
        setBulkNotice(
          `Checked ${targets.length} overdue invoice${targets.length === 1 ? "" : "s"}: ${matched} have a reminder due. Review and send from Payment reminders.`,
        );
      }
      setSelectedIds(new Set());
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBulkBusy(false);
      setBulkConfirm(null);
    }
  };

  const advancedFilterCount =
    (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (sort !== "due" ? 1 : 0);

  const clearAdvancedFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSort("due");
  };

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    clearDocumentDraft(INVOICE_DRAFT_PREFIX, organizationId);
  };

  // Mint a token-scoped public invoice link and copy it. Link affordance
  // only — there is no customer-facing portal UI in this app; the URL opens
  // the backend's view-only `GET /portal/{token}/invoice` route.
  const copyPortalLink = async (invoiceId: string) => {
    setPortalBusy(invoiceId);
    setPortalLink(null);
    setError(null);
    try {
      const minted = await createInvoicePortalToken(invoiceId);
      const url = portalInvoiceUrl(minted.token);
      try {
        await navigator.clipboard.writeText(url);
        setPortalLink(`Customer link copied: ${url}`);
      } catch {
        setPortalLink(`Customer link (copy manually): ${url}`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setPortalBusy(null);
    }
  };

  // Rendered invoice PDF (`GET /invoices/{id}.pdf`) via the shared
  // Bearer-blob download.
  const downloadPdf = async (invoiceId: string) => {
    setPdfBusy(invoiceId);
    setError(null);
    try {
      await downloadInvoicePdf(invoiceId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setPdfBusy(null);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const name = draft.partyName.trim();
      if (!name) throw new Error("Customer name is required.");

      const dueIso = draft.dueAt ? dateInputToUtcIso(draft.dueAt) : undefined;
      if (draft.dueAt && !dueIso)
        throw new Error("Due date must be a valid date.");
      if (!draft.issueDate || !dateInputToUtcIso(draft.issueDate))
        throw new Error("Issue date is required and must be a valid date.");
      if (!draft.dueAt && !draft.crmContactId && !draft.paymentTermId)
        throw new Error(
          "Choose a customer record or payment term, or enter a due-date override.",
        );
      if (!draft.dueAt && draft.paymentTermId === "none" && !draft.crmContactId)
        throw new Error(
          "Enter a due date when no customer record is selected.",
        );
      const payloadLines = toInvoiceLinePayload(draft.lines);

      await createInvoice({
        customer_name: name,
        ...(dueIso ? { due_at: dueIso } : {}),
        issue_date: draft.issueDate,
        ...(draft.paymentTermId
          ? {
              payment_term_id:
                draft.paymentTermId === "none" ? null : draft.paymentTermId,
            }
          : {}),
        ...(draft.crmContactId ? { crm_contact_id: draft.crmContactId } : {}),
        lines: payloadLines,
      });
      clearDraft();
      setCreatorOpen(false);
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const rowActions = (invoice: Invoice) => (
    <InvoiceRowActions
      invoice={invoice}
      onChanged={reload}
      onCopyLink={(invoiceId) => void copyPortalLink(invoiceId)}
      onDownloadPdf={(invoiceId) => void downloadPdf(invoiceId)}
      onPrepaymentApplied={(journalEntryId) => {
        setApplyNotice(true);
        setAppliedJournalId(journalEntryId ?? null);
      }}
      onCreateCreditNote={setCreditInvoiceId}
      copyBusy={portalBusy === invoice.id}
      pdfBusy={pdfBusy === invoice.id}
    />
  );

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Invoices"
        description="Customer invoices and repeat billing."
        actions={
          viewTab === "invoices" ? (
            <Button type="button" onClick={() => setCreatorOpen(true)}>
              New invoice
            </Button>
          ) : undefined
        }
      />

      <Tabs
        label="Invoice views"
        tabs={[
          { value: "invoices", label: "Invoices" },
          { value: "recurring", label: "Recurring invoices" },
        ]}
        value={viewTab}
        onValueChange={(value) =>
          setViewTab(value === "recurring" ? "recurring" : "invoices")
        }
      >
        {viewTab === "recurring" ? (
          <RecurringInvoicesPage embedded />
        ) : (
          <div className="grid gap-6">
            <WorklistSummary
              title="Invoice summary"
              testId="invoices-summary"
              loading={summaryLoading}
              error={summaryError}
              onRetry={loadSummary}
              totalCount={summary?.total_count ?? 0}
              openCount={summary?.open_count ?? 0}
              outstandingCents={summary?.outstanding_cents ?? 0}
              overdueCents={summary?.overdue_cents ?? 0}
              aging={summary?.aging ?? []}
              currency={listCurrency}
              showOpen={false}
              showAging={false}
              outstandingSelected={statusFilter === "sent"}
              overdueSelected={statusFilter === "overdue"}
              onSelectOutstanding={() => setStatusFilter("sent")}
              onSelectOverdue={() => setStatusFilter("overdue")}
            />

            {applyNotice ? (
              <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
                The invoice balance was reduced.{" "}
                {appliedJournalId ? (
                  <Link
                    to="/journal/$entryId"
                    params={{ entryId: appliedJournalId }}
                    className="underline"
                  >
                    View the journal
                  </Link>
                ) : null}
              </output>
            ) : null}

            {bulkNotice ? (
              <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
                {bulkNotice}
              </output>
            ) : null}

            {creditNotice ? (
              <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
                {creditNotice}
              </output>
            ) : null}

            {error ? (
              <div
                role="alert"
                className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
              >
                {error}
                {error.includes("No Console organization context") ? (
                  <p className="mt-1 text-[var(--muted-foreground)]">
                    Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
                  </p>
                ) : null}
              </div>
            ) : null}

            {portalLink ? (
              <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm break-all text-[var(--success)]">
                {portalLink}
              </output>
            ) : null}

            <DataTableBlock<Invoice>
              rows={visibleInvoices}
              columns={[
                {
                  key: "number",
                  header: "Number",
                  className: "font-mono text-xs",
                  cell: (invoice) => (
                    <Link
                      to="/invoices/$invoiceId"
                      params={{ invoiceId: invoice.id }}
                      className="hover:underline"
                    >
                      {invoice.invoice_number}
                    </Link>
                  ),
                },
                {
                  key: "customer",
                  header: "Customer",
                  cell: (invoice) => invoice.customer_name,
                },
                {
                  key: "due",
                  header: "Due",
                  cell: (invoice) =>
                    new Date(invoice.due_at).toLocaleDateString(),
                },
                {
                  key: "status",
                  header: "Status",
                  cell: (invoice) => {
                    const label = invoiceListStatus(invoice);
                    return <Badge tone={statusTone(label)}>{label}</Badge>;
                  },
                },
                {
                  key: "credit-note",
                  header: "Credit note",
                  cell: (invoice) => (
                    <InvoiceCreditNoteCell
                      notes={creditNotesByInvoice.get(invoice.id) ?? []}
                      invoiceId={invoice.id}
                    />
                  ),
                },
                {
                  key: "balance",
                  header: "Balance",
                  align: "end",
                  className: "tabular-nums",
                  cell: (invoice) =>
                    formatMoney({
                      cents: payableBalanceCents(invoice),
                      currency: invoice.currency_code || "USD",
                    }),
                },
                {
                  key: "total",
                  header: "Total",
                  align: "end",
                  className: "tabular-nums",
                  cell: (invoice) =>
                    formatMoney({
                      cents: invoice.total_minor,
                      currency: invoice.currency_code || "USD",
                    }),
                },
                {
                  key: "actions",
                  header: "Actions",
                  align: "end",
                  cell: (invoice) => (
                    <div className="flex justify-end gap-1">
                      {rowActions(invoice)}
                    </div>
                  ),
                },
              ]}
              getRowId={(invoice) => invoice.id}
              selectedIds={selectedIds}
              onSelectedIdsChange={(next) => setSelectedIds(next)}
              isRowSelectable={isSelectableInvoice}
              selectAllLabel="Select all eligible invoices"
              getRowLabel={(invoice) => `invoice ${invoice.invoice_number}`}
              loading={invoices === null}
              filtered={
                visibleInvoices.length === 0 && (invoices?.length ?? 0) > 0
              }
              emptyState={
                <p className="text-sm text-[var(--muted-foreground)]">
                  No invoices yet — create the first one.
                </p>
              }
              filteredEmptyState={
                <p className="text-sm text-[var(--muted-foreground)]">
                  No invoices match these filters.
                </p>
              }
              search={
                <label
                  htmlFor="invoices-search"
                  className="grid gap-1 text-xs font-medium"
                >
                  Search
                  <Input
                    id="invoices-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Number or customer…"
                  />
                </label>
              }
              filters={
                <label
                  htmlFor="invoices-status"
                  className="grid min-w-40 gap-1 text-xs font-medium"
                >
                  Status
                  <select
                    id="invoices-status"
                    className="select"
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value === "draft" ||
                          e.target.value === "sent" ||
                          e.target.value === "overdue" ||
                          e.target.value === "paid" ||
                          e.target.value === "void"
                          ? e.target.value
                          : "",
                      )
                    }
                  >
                    <option value="">
                      All statuses ({(invoices ?? []).length})
                    </option>
                    <option value="draft">
                      Draft ({statusCounts.draft ?? 0})
                    </option>
                    <option value="sent">
                      Awaiting payment ({sentCount})
                    </option>
                    <option value="overdue">Overdue ({overdueCount})</option>
                    <option value="paid">
                      Paid ({statusCounts.paid ?? 0})
                    </option>
                    <option value="void">Void ({statusCounts.void ?? 0})</option>
                  </select>
                </label>
              }
              tools={
                <Button
                  type="button"
                  variant="secondary"
                  aria-expanded={filtersOpen}
                  onClick={() => setFiltersOpen((open) => !open)}
                >
                  Filters
                  {advancedFilterCount > 0 ? ` (${advancedFilterCount})` : ""}
                </Button>
              }
              extensionTools={null}
              actions={
                advancedFilterCount > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={clearAdvancedFilters}
                  >
                    Clear
                  </Button>
                ) : null
              }
              advancedFilters={
                filtersOpen ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <label
                      htmlFor="invoices-from"
                      className="grid gap-1 text-xs font-medium"
                    >
                      Due from
                      <Input
                        id="invoices-from"
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </label>
                    <label
                      htmlFor="invoices-to"
                      className="grid gap-1 text-xs font-medium"
                    >
                      Due to
                      <Input
                        id="invoices-to"
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </label>
                    <label
                      htmlFor="invoices-sort"
                      className="grid gap-1 text-xs font-medium"
                    >
                      Sort by
                      <select
                        id="invoices-sort"
                        className="select"
                        value={sort}
                        onChange={(e) => setSort(e.target.value as typeof sort)}
                      >
                        <option value="due">Due date</option>
                        <option value="total">Total</option>
                        <option value="number">Number</option>
                        <option value="customer">Customer</option>
                      </select>
                    </label>
                  </div>
                ) : null
              }
              onRefresh={reload}
              bulkActions={bulkActions}
              tableLabel="Invoices"
              testId="invoices"
              mobileClassName="stack-list"
              renderMobileRow={(invoice, context) => {
                const currency = invoice.currency_code || "USD";
                const label = invoiceListStatus(invoice);
                return (
                  <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      {context.selectable ? (
                        <label className="flex items-center gap-2">
                          {context.selectionCheckbox}
                          <Link
                            to="/invoices/$invoiceId"
                            params={{ invoiceId: invoice.id }}
                            className="record-title underline-offset-4 hover:underline"
                          >
                            {invoice.invoice_number}
                          </Link>
                        </label>
                      ) : (
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: invoice.id }}
                          className="record-title underline-offset-4 hover:underline"
                        >
                          {invoice.invoice_number}
                        </Link>
                      )}
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: invoice.total_minor,
                          currency,
                        })}
                      </span>
                    </div>
                    <CardField label="Customer">
                      {invoice.customer_name}
                    </CardField>
                    <CardField label="Due">
                      {new Date(invoice.due_at).toLocaleDateString()}
                    </CardField>
                    <CardField label="Status">
                      <Badge
                        tone={statusTone(label)}
                        className="justify-self-start"
                      >
                        {label}
                      </Badge>
                    </CardField>
                    <CardField label="Credit note">
                      <InvoiceCreditNoteCell
                        notes={creditNotesByInvoice.get(invoice.id) ?? []}
                        invoiceId={invoice.id}
                      />
                    </CardField>
                    <CardField label="Still owed">
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: payableBalanceCents(invoice),
                          currency,
                        })}
                      </span>
                    </CardField>
                    <div className="flex flex-wrap gap-2 pt-3">
                      {rowActions(invoice)}
                    </div>
                  </article>
                );
              }}
            />
          </div>
        )}
      </Tabs>

      <Drawer
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        title="New invoice"
        description="Bill a customer. Closing this panel keeps your draft in this browser session."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreatorOpen(false)}
            >
              Back to invoices
            </Button>
            {draft.partyName ||
            draft.lines.some((line) => line.description || line.amount) ? (
              <Button type="button" variant="ghost" onClick={clearDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="invoice-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Creating…" : "Create invoice"}
            </Button>
          </>
        }
      >
        <form
          id="invoice-create-form"
          onSubmit={submit}
          className="grid gap-5"
          aria-label="Create invoice"
        >
          {error ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {error}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label
              htmlFor="invoice-customer"
              className="grid gap-1 text-xs font-medium"
            >
              Customer
              <Input
                id="invoice-customer"
                list="invoice-customer-options"
                value={draft.partyName}
                onChange={(e) => selectCustomer(e.target.value)}
                required
                placeholder="Search or type a name"
                autoComplete="off"
              />
              <datalist id="invoice-customer-options">
                {knownCustomers.map((customer) => (
                  <option key={customer.name} value={customer.name} />
                ))}
              </datalist>
              <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                Pick a past customer or type a new one.
              </span>
            </label>
            <label
              htmlFor="invoice-issue-date"
              className="grid gap-1 text-xs font-medium"
            >
              Issue date
              <Input
                id="invoice-issue-date"
                type="date"
                value={draft.issueDate}
                onChange={(e) => updateDraft({ issueDate: e.target.value })}
                required
              />
            </label>
            <label
              htmlFor="invoice-payment-term"
              className="grid gap-1 text-xs font-medium"
            >
              Payment term
              <PaymentTermPicker
                id="invoice-payment-term"
                value={draft.paymentTermId}
                onChange={(value) => updateDraft({ paymentTermId: value })}
                terms={paymentTerms}
                includeContactDefault
              />
            </label>
            <label
              htmlFor="invoice-due"
              className="grid gap-1 text-xs font-medium"
            >
              Due-date override (optional)
              <Input
                id="invoice-due"
                type="date"
                value={draft.dueAt}
                onChange={(e) => updateDraft({ dueAt: e.target.value })}
              />
            </label>
          </div>
          {paymentTermsError ? (
            <p className="text-xs text-[var(--danger)]">
              Could not load payment terms: {paymentTermsError}
            </p>
          ) : null}

          <fieldset className="grid gap-3">
            <legend className="text-xs font-medium">Lines</legend>
            {draft.lines.map((line, index) => (
              <DocumentLineRow
                key={line.uid}
                idPrefix={`invoice-line-${index}`}
                index={index}
                line={line}
                onChange={(patch) => updateLine(index, patch)}
                onRemove={() => removeLine(index)}
                canRemove={draft.lines.length > 1}
              />
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines: [...prev.lines, emptyDraftLine()],
                  }))
                }
              >
                Add line
              </Button>
            </div>
          </fieldset>

          <p
            className="text-sm text-[var(--muted-foreground)]"
            aria-live="polite"
            data-testid="invoice-totals"
          >
            Subtotal ({draft.lines.length}{" "}
            {draft.lines.length === 1 ? "line" : "lines"}):{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.subtotalCents, currency: "USD" })}
            </span>{" "}
            — Tax (estimated):{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.taxCents, currency: "USD" })}
            </span>{" "}
            — Total:{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: totals.totalCents, currency: "USD" })}
            </span>
          </p>
        </form>
      </Drawer>

      <Dialog
        open={bulkConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setBulkConfirm(null);
        }}
        title={
          bulkConfirm === "mark-sent"
            ? "Mark drafts as sent?"
            : "Check payment reminders?"
        }
        description={
          bulkConfirm === "mark-sent"
            ? `Moves ${draftTargets.length} draft invoice${draftTargets.length === 1 ? "" : "s"} to sent. This changes the invoice status.`
            : `Checks the reminder rules for ${reminderTargets.length} overdue invoice${reminderTargets.length === 1 ? "" : "s"}. Nothing is emailed from this page; review and send from Payment reminders.`
        }
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setBulkConfirm(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void runBulkAction()}
            disabled={bulkBusy}
          >
            {bulkBusy
              ? "Working…"
              : bulkConfirm === "mark-sent"
                ? "Mark as sent"
                : "Check reminders"}
          </Button>
        </div>
      </Dialog>

      <CreditNoteCreateDrawer
        open={creditInvoiceId !== null}
        initialInvoiceId={creditInvoiceId ?? undefined}
        onOpenChange={(open) => {
          if (!open) setCreditInvoiceId(null);
        }}
        closeLabel="Back to invoices"
        onCreated={() => {
          setCreditInvoiceId(null);
          setCreditNotice("Credit note created as a draft for this invoice.");
          reload();
        }}
      />
    </div>
  );
}
